import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  computeHasMore,
  dedupeByKey,
  filterOptionsByQuery,
  injectSelectedOption,
} from './selectUtils';

const defaultGetOptionId = (option) => option?.id;
const defaultGetOptionLabel = (option) => option?.label ?? option?.name ?? '';
const defaultIsEqual = (option, value) =>
  String(defaultGetOptionId(option)) === String(defaultGetOptionId(value));

function isAbortError(err) {
  return (
    err?.name === 'AbortError' ||
    err?.name === 'CanceledError' ||
    err?.code === 'ERR_CANCELED'
  );
}

/**
 * Paginated infinite select hook.
 * - browse: open + scroll loads pages (search="")
 * - local: filters loaded browse cache instantly (no API) — hybrid strategy only
 * - remote: server search (always for remote-only strategy)
 */
export default function useInfiniteOptions({
  fetchPage,
  extraParams,
  pageSize = 20,
  enabled = true,
  loadOnOpen = true,
  selectedValue = null,
  isOptionEqualToValue,
  getOptionId = defaultGetOptionId,
  getOptionLabel = defaultGetOptionLabel,
  enableSearch = false,
  minRemoteSearchLength = 2,
  searchDebounceMs = 350,
  searchStrategy = 'hybrid',
  injectSelectedWhileSearching,
}) {
  const resolvedInjectWhileSearching =
    injectSelectedWhileSearching ?? searchStrategy === 'hybrid';

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [searchMode, setSearchMode] = useState('browse');
  const [searchQuery, setSearchQuery] = useState('');

  const pageRef = useRef(1);
  const fetchSeqRef = useRef(0);
  const loadingCountRef = useRef(0);
  const loadingMoreCountRef = useRef(0);
  const hasLoadedOnceRef = useRef(false);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  const browseCacheRef = useRef([]);
  const browseHasMoreRef = useRef(false);
  const searchModeRef = useRef('browse');
  const searchQueryRef = useRef('');
  const searchStrategyRef = useRef(searchStrategy);
  const injectWhileSearchingRef = useRef(resolvedInjectWhileSearching);

  const fetchPageRef = useRef(fetchPage);
  const extraParamsRef = useRef(extraParams);
  const selectedValueRef = useRef(selectedValue);
  const getOptionIdRef = useRef(getOptionId);
  const getOptionLabelRef = useRef(getOptionLabel);
  const isEqualRef = useRef(isOptionEqualToValue ?? defaultIsEqual);
  const selectedValueId = selectedValue?.id ?? null;

  fetchPageRef.current = fetchPage;
  extraParamsRef.current = extraParams;
  selectedValueRef.current = selectedValue;
  getOptionIdRef.current = getOptionId;
  getOptionLabelRef.current = getOptionLabel;
  isEqualRef.current = isOptionEqualToValue ?? defaultIsEqual;
  searchModeRef.current = searchMode;
  searchQueryRef.current = searchQuery;
  searchStrategyRef.current = searchStrategy;
  injectWhileSearchingRef.current = resolvedInjectWhileSearching;

  const extraParamsKey = useMemo(
    () => JSON.stringify(extraParams ?? {}),
    [extraParams]
  );

  const shouldInjectSelected = useCallback(() => {
    if (searchModeRef.current === 'browse') return true;
    return injectWhileSearchingRef.current;
  }, []);

  const withSelectedIfNeeded = useCallback((items) => {
    if (!shouldInjectSelected()) return items;
    return injectSelectedOption(items, selectedValueRef.current, isEqualRef.current);
  }, [shouldInjectSelected]);

  const beginLoading = useCallback((isFirstPage) => {
    if (isFirstPage) {
      loadingCountRef.current += 1;
      setLoading(true);
      return;
    }
    loadingMoreCountRef.current += 1;
    setLoadingMore(true);
  }, []);

  const endLoading = useCallback((isFirstPage) => {
    if (isFirstPage) {
      loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
      if (loadingCountRef.current === 0) {
        setLoading(false);
      }
      return;
    }
    loadingMoreCountRef.current = Math.max(0, loadingMoreCountRef.current - 1);
    if (loadingMoreCountRef.current === 0) {
      setLoadingMore(false);
    }
  }, []);

  const cancelInFlight = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const applyBrowseCache = useCallback((items, nextHasMore, page) => {
    browseCacheRef.current = items;
    browseHasMoreRef.current = nextHasMore;
    pageRef.current = page;
    if (searchModeRef.current === 'browse') {
      setOptions(withSelectedIfNeeded(items));
      setHasMore(nextHasMore);
    }
  }, [withSelectedIfNeeded]);

  const runFetchRef = useRef(null);

  runFetchRef.current = async ({ page, append = false, search = '' }) => {
    if (!enabled || !fetchPageRef.current) return;

    if (!append) {
      cancelInFlight();
    }

    const controller = new AbortController();
    if (!append) {
      abortRef.current = controller;
    }

    const seq = ++fetchSeqRef.current;
    const isFirstPage = !append;

    beginLoading(isFirstPage);
    setError(null);

    try {
      const result = await fetchPageRef.current({
        page,
        pageSize,
        search,
        extraParams: extraParamsRef.current,
        signal: controller.signal,
      });

      if (seq !== fetchSeqRef.current) return;

      const items = Array.isArray(result?.items) ? result.items : [];
      const nextHasMore =
        result?.hasMore ??
        computeHasMore({
          page,
          pageSize,
          itemsLength: items.length,
          totalCount: result?.totalCount,
        });

      const isBrowseFetch = !search;

      if (isBrowseFetch) {
        const merged = append
          ? dedupeByKey([...browseCacheRef.current, ...items], getOptionIdRef.current)
          : dedupeByKey(items, getOptionIdRef.current);
        applyBrowseCache(merged, nextHasMore, page);
      } else {
        setOptions((prev) => {
          const merged = append
            ? dedupeByKey([...prev, ...items], getOptionIdRef.current)
            : dedupeByKey(items, getOptionIdRef.current);
          return withSelectedIfNeeded(merged);
        });
        setHasMore(nextHasMore);
        pageRef.current = page;
      }

      hasLoadedOnceRef.current = true;
    } catch (err) {
      if (isAbortError(err) || seq !== fetchSeqRef.current) return;
      setError(err?.message || 'Failed to load options');
      if (!append && search) {
        setOptions(withSelectedIfNeeded([]));
      } else if (!append && !search) {
        setOptions(withSelectedIfNeeded([]));
        browseCacheRef.current = [];
      }
      setHasMore(false);
    } finally {
      endLoading(isFirstPage);
    }
  };

  const loadBrowseFirstPage = useCallback(() => {
    pageRef.current = 1;
    hasLoadedOnceRef.current = false;
    fetchSeqRef.current += 1;
    searchModeRef.current = 'browse';
    searchQueryRef.current = '';
    setSearchMode('browse');
    setSearchQuery('');
    void runFetchRef.current?.({ page: 1, append: false, search: '' });
  }, []);

  useEffect(() => {
    if (!enabled) {
      setOptions([]);
      setHasMore(false);
      setError(null);
      setSearchMode('browse');
      setSearchQuery('');
      hasLoadedOnceRef.current = false;
      browseCacheRef.current = [];
      browseHasMoreRef.current = false;
      cancelInFlight();
      return undefined;
    }

    pageRef.current = 1;
    hasLoadedOnceRef.current = false;
    fetchSeqRef.current += 1;
    browseCacheRef.current = [];
    browseHasMoreRef.current = false;
    searchModeRef.current = 'browse';
    searchQueryRef.current = '';
    setSearchMode('browse');
    setSearchQuery('');
    cancelInFlight();

    if (!loadOnOpen) {
      void runFetchRef.current?.({ page: 1, append: false, search: '' });
    } else {
      setOptions([]);
      setHasMore(false);
    }

    return undefined;
  }, [cancelInFlight, enabled, extraParamsKey, loadOnOpen]);

  useEffect(() => {
    if (!shouldInjectSelected()) return undefined;

    setOptions((prev) => {
      const next = withSelectedIfNeeded(prev);
      return next === prev ? prev : next;
    });
    return undefined;
  }, [selectedValueId, shouldInjectSelected, withSelectedIfNeeded]);

  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      cancelInFlight();
    },
    [cancelInFlight]
  );

  const handleOpen = useCallback(() => {
    if (!enabled || !loadOnOpen) return;
    if (hasLoadedOnceRef.current || loadingCountRef.current > 0) return;
    void runFetchRef.current?.({ page: 1, append: false, search: '' });
  }, [enabled, loadOnOpen]);

  const handleLoadMore = useCallback(() => {
    if (!enabled || !hasMore || loadingCountRef.current > 0 || loadingMoreCountRef.current > 0) {
      return;
    }

    if (searchModeRef.current === 'local') {
      return;
    }

    const nextPage = pageRef.current + 1;
    const search = searchModeRef.current === 'remote' ? searchQueryRef.current : '';
    void runFetchRef.current?.({ page: nextPage, append: true, search });
  }, [enabled, hasMore]);

  const applyLocalSearch = useCallback((query) => {
    const localMatches = filterOptionsByQuery(
      browseCacheRef.current,
      query,
      getOptionLabelRef.current
    );
    searchModeRef.current = 'local';
    searchQueryRef.current = query;
    setSearchMode('local');
    setSearchQuery(query);
    setOptions(withSelectedIfNeeded(localMatches));
    setHasMore(false);
  }, [withSelectedIfNeeded]);

  const applyRemoteSearch = useCallback((query) => {
    searchModeRef.current = 'remote';
    searchQueryRef.current = query;
    setSearchMode('remote');
    setSearchQuery(query);
    pageRef.current = 1;
    fetchSeqRef.current += 1;
    setOptions([]);
    setHasMore(false);
    void runFetchRef.current?.({ page: 1, append: false, search: query });
  }, []);

  const applyShortQuerySearch = useCallback((query) => {
    searchModeRef.current = 'remote';
    searchQueryRef.current = query;
    setSearchMode('remote');
    setSearchQuery(query);
    setOptions([]);
    setHasMore(false);
  }, []);

  const resetBrowseView = useCallback(() => {
    searchModeRef.current = 'browse';
    searchQueryRef.current = '';
    setSearchMode('browse');
    setSearchQuery('');
    setOptions(withSelectedIfNeeded(browseCacheRef.current));
    setHasMore(browseHasMoreRef.current);
    pageRef.current = Math.max(1, pageRef.current);
  }, [withSelectedIfNeeded]);

  const handleSearchInputChange = useCallback((rawQuery) => {
    if (!enableSearch) return;

    const trimmedQuery = String(rawQuery ?? '').trim();
    const isRemoteOnly = searchStrategyRef.current === 'remote-only';

    if (isRemoteOnly && trimmedQuery) {
      setOptions([]);
      setHasMore(false);
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const query = trimmedQuery;

      if (!query) {
        resetBrowseView();
        return;
      }

      if (isRemoteOnly) {
        if (query.length < minRemoteSearchLength) {
          applyShortQuerySearch(query);
          return;
        }
        applyRemoteSearch(query);
        return;
      }

      const localMatches = filterOptionsByQuery(
        browseCacheRef.current,
        query,
        getOptionLabelRef.current
      );

      if (localMatches.length > 0) {
        applyLocalSearch(query);
        return;
      }

      if (query.length < minRemoteSearchLength) {
        applyLocalSearch(query);
        return;
      }

      applyRemoteSearch(query);
    }, searchDebounceMs);
  }, [
    applyLocalSearch,
    applyRemoteSearch,
    applyShortQuerySearch,
    enableSearch,
    minRemoteSearchLength,
    resetBrowseView,
    searchDebounceMs,
  ]);

  const handleClose = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    resetBrowseView();
  }, [resetBrowseView]);

  const refresh = useCallback(() => {
    loadBrowseFirstPage();
  }, [loadBrowseFirstPage]);

  return {
    options,
    loading,
    loadingMore,
    error,
    hasMore,
    searchMode,
    searchQuery,
    enableSearch,
    onOpen: handleOpen,
    onLoadMore: handleLoadMore,
    onClose: handleClose,
    onSearchInputChange: enableSearch ? handleSearchInputChange : undefined,
    refresh,
  };
};
